import type { TextInputProps } from "@codat/orchard-ui/dist/es/components/TextInput/TextInput";
import * as EmailValidator from "email-validator";
import React, { useState } from "react";

import TextInput from "components/TextInput";

interface EmailInputProps extends Omit<TextInputProps, "label" | "id"> {
  setEmail: (password: string) => void;
  value: string;
  isEmailValid: (isValid: boolean) => void;
  displayErrors: boolean;
  label?: string;
  id?: string;
}

export enum InvalidEmailErrorMessages {
  EmptyEmail = "Please enter an email",
  EmailTooShort = "Minimum length is 3 chars",
  EmailTooLong = "Maximum length is 256 chars",
  InvalidEmailFormat = "Please enter a valid email",
}

const getEmailError = (input: string): InvalidEmailErrorMessages | "" => {
  if (!input) {
    return InvalidEmailErrorMessages.EmptyEmail;
  } else if (input.length < 3) {
    return InvalidEmailErrorMessages.EmailTooShort;
  } else if (input.length > 256) {
    return InvalidEmailErrorMessages.EmailTooLong;
  } else if (!EmailValidator.validate(input)) {
    return InvalidEmailErrorMessages.InvalidEmailFormat;
  }

  return "";
};

const EmailInput: React.FC<EmailInputProps> = ({
  setEmail,
  value,
  isEmailValid,
  displayErrors,
  ...otherProps
}) => {
  const [emailError, setEmailError] = useState(getEmailError(value));

  const emailUpdate: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const email = event.target.value;
    setEmail(email);
    const newEmailError = getEmailError(email);
    setEmailError(newEmailError);
    isEmailValid(newEmailError.length === 0);
  };

  return (
    <TextInput
      inputName="Email Input"
      data-testid="email-input"
      id="email-input"
      label="Email address"
      onChange={emailUpdate}
      value={value}
      error={displayErrors && emailError.length > 0}
      errorMessage={emailError}
      autoComplete="username"
      {...otherProps}
    />
  );
};

export default EmailInput;
